import React, { forwardRef, useState, useRef, useEffect } from 'react'

// Renders an input visually flanked by a static prefix and/or suffix that
// appears inline with the typed text (e.g. "$ 100.00 USD"). The trick: a
// transparent absolutely-positioned mirror has the same className as the
// input (so identical font/padding/line-height) and contains the visible
// prefix/suffix plus an invisible placeholder for the input's value. JS
// reads the prefix/suffix widths from the mirror and feeds them into the
// real input's padding-inline-start/end, so the input's text lands exactly
// between the visible prefix and suffix. The mirror is aria-hidden and
// pointer-events-none so it has no semantic or interactive presence.
//
// The input className keeps .form-control so it inherits all --input-*
// tokens. Inside .input-group, wrap the SandwichField in a
// .input-group-cell that owns the flex layout (e.g. `flex-1 min-w-0`)
// and mark the input .input-group-control so the wrapper strips its
// border/bg/radius. The default containerClassName ('relative') is the
// only thing the mirror's absolute positioning needs.

const SandwichField = forwardRef(({
  className = 'form-control',
  containerClassName = 'relative',
  style,
  forwardType = 'text', type = forwardType,
  prefix, suffix,
  ...props
}, ref) => {
  const [ padding, setPadding ] = useState({})

  const mirror = useRef()
  const prefixRef = useRef()
  const suffixRef = useRef()

  const inputProps = {
    ...props, ref, className,
    style: { ...style, ...padding, outline: 0 },
  }

  const inputMarkup = [ 'function', 'object' ].includes(typeof type)
    ? (Input => <Input {...inputProps} />)(type)
    : <input {...inputProps} type={type} />

  const dir = type.dir
    || ([ 'email', 'number', 'tel', 'url' ].includes(type) ? 'ltr' : null)

  useEffect(() => {
    const calcPadding = () => {
      // The IntersectionObserver below delivers callbacks asynchronously, so
      // one can fire after the field has unmounted (React has nulled the ref
      // and disconnect() doesn't cancel an already-queued notification) —
      // guard against getComputedStyle(null). A live mirror implies its
      // prefix/suffix children exist too, so the offsetWidth reads are safe.
      if (!mirror.current) return
      const {
        paddingInlineStart,
        paddingInlineEnd,
        paddingBlockStart,
        paddingBlockEnd,
      } = getComputedStyle(mirror.current)
      setPadding({
        paddingInlineStart: `calc(${paddingInlineStart} + ${prefixRef.current.offsetWidth}px)`,
        paddingInlineEnd: `calc(${paddingInlineEnd} + ${suffixRef.current.offsetWidth}px)`,
        paddingBlockStart,
        paddingBlockEnd,
      })
    }
    calcPadding()
    // Re-measure when the field becomes visible (e.g. when the panel it
    // lives in scrolls into view), since offsetWidth reads zero offscreen.
    const observer = new IntersectionObserver(entries =>
      entries.forEach(entry => {
        if (entry.isIntersecting) calcPadding()
      })
    )
    observer.observe(mirror.current)
    return () => observer.disconnect()
  }, [ className, JSON.stringify(style), prefix, suffix ])

  return (
    <div className={containerClassName}>
      <div
        ref={mirror}
        dir={dir}
        className={`absolute w-full pointer-events-none ${className}`}
        style={{
          ...style,
          borderColor: 'transparent',
          backgroundColor: 'transparent',
          backgroundImage: 'none',
        }}
        aria-hidden="true"
      >
        <span ref={prefixRef}>{prefix}</span>
        <span className="opacity-0">{props.value || props.placeholder}</span>
        <span ref={suffixRef}>{suffix}</span>
      </div>
      {inputMarkup}
    </div>
  )
})

SandwichField.displayName = 'SandwichField'

export default SandwichField
